/** @file flatBackgroundOf: the four corner colours of an opaque flat background, or null when they disagree. */
import type { Image, Rgba } from '../types/image';

/** The largest channel difference between two colours (RGB only). */
const spread = (a: Rgba, b: Rgba): number => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));

/**
 * The colours of the four corner pixels when every one is opaque and no two differ by more than `tolerance` in any
 * channel (a phone screenshot on black, a white page); null otherwise (a transparent sheet needs no background).
 */
export function flatBackgroundOf(image: Image, tolerance: number): Rgba[] | null {
  const { width: w, height: h, data } = image;
  if (w === 0 || h === 0) return null;
  const at = (x: number, y: number): Rgba => {
    const i = (y * w + x) * 4;
    return [data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0, data[i + 3] ?? 0];
  };
  const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
  if (corners.some((c) => c[3] < 255)) return null;
  const agree = corners.every((a) => corners.every((b) => spread(a, b) <= tolerance));
  return agree ? corners : null;
}
